/**
 * Sector workspaces: the admin console works in one sector at a time
 * (docs/ARCHITECTURE.md).
 *
 * The sector is in the address (`/rental/customers`). Pages below it read it
 * with `useSector()` and pass its code to every list they load; the server
 * refuses a list that names none. The switch at the top of the sidebar moves
 * between sectors and keeps the page when the other sector has it.
 */
import { createContext, useContext } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { VerticalInfo } from '@lipaflow/shared'
import { adminService } from '../services/admin'
import { SECTOR_PAGES, type SectorPage } from './pages'

export interface Sector {
  id: string
  code: string
  name: string
}

export const SectorContext = createContext<Sector | null>(null)

/** The sector the page works in. Only inside `/:sector/…`. */
export function useSector(): Sector {
  const sector = useContext(SectorContext)
  if (!sector) throw new Error('useSector() outside a sector workspace')
  return sector
}

/** The sector the page works in, or null on a platform page. */
export function useOptionalSector(): Sector | null {
  return useContext(SectorContext)
}

/** Every active sector, by name. */
export function useSectors() {
  return useQuery({
    queryKey: ['sectors'],
    queryFn: adminService.listVerticals,
    staleTime: 5 * 60 * 1000,
    select: (verticals: VerticalInfo[]): Sector[] =>
      verticals
        .map(({ id, code, name }) => ({ id, code, name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
  })
}

const LAST_SECTOR = 'lipaflow.admin.sector'

/** The sector last worked in, on this browser. A convenience: never trusted. */
export function lastSectorCode(): string | null {
  try {
    return localStorage.getItem(LAST_SECTOR)
  } catch {
    return null
  }
}

export function rememberSector(code: string) {
  try {
    localStorage.setItem(LAST_SECTOR, code)
  } catch {
    // Private windows and blocked storage: the address still carries the sector.
  }
}

export function sectorPath(code: string, page = ''): string {
  return page ? `/${code}/${page}` : `/${code}`
}

/** The page the address is on inside a sector (`customers`, `onboarding` …), if any. */
export function currentSectorPage(pathname: string): SectorPage | undefined {
  const [, , first = ''] = pathname.split('/')
  return SECTOR_PAGES.find((page) => page.path === first)
}

/**
 * Where the switch goes: the same page in the other sector when it has it,
 * else that sector's Home.
 */
export function switchTarget(pathname: string, to: string): string {
  const page = currentSectorPage(pathname)
  if (page && page.path && (!page.sectors || page.sectors.includes(to))) return sectorPath(to, page.path)
  return sectorPath(to)
}

